/**
 * @file /app/resume-check/ResumeCheckContent.tsx
 *
 * @description Client component for the /resume-check standalone tool page.
 *
 * Three phases:
 *   "input"   - resume (uploaded, pasted or a sample) and a required job description
 *   "scoring" - one request to /api/landing/resume-check/score, with rotating messages
 *   "complete" - the match card (score, keywords, competencies) and a sign-up CTA
 *
 * No auth required. Public page.
 */

"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { RotatingMessages } from "@/components/ui/RotatingMessages";
import { InputPhase, ResultsPhase } from "./phases";
import { clientLogger } from "@/lib/logger-client";
import type { ResumeMatchReport } from "@/types";

type Phase = "input" | "scoring" | "complete";

const SCORING_MESSAGES = [
  "Reading your resume",
  "Reading the job description",
  "Checking every requirement",
  "Scoring your match",
];

export default function ResumeCheckContent() {
  const [phase, setPhase] = useState<Phase>("input");
  const [resumeText, setResumeText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [report, setReport] = useState<ResumeMatchReport | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);

  const handleSubmit = async (resText: string, jobDesc: string) => {
    setResumeText(resText);
    setJobDescription(jobDesc);
    setReport(null);
    setFatalError(null);
    setPhase("scoring");

    try {
      const res = await fetch("/api/landing/resume-check/score", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeText: resText, jobDescription: jobDesc }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Could not score your resume. Please try again.");
      }
      const data = await res.json();
      setReport(data.matchReport as ResumeMatchReport);
      setPhase("complete");
    } catch (error) {
      const message = error instanceof Error ? error.message : "An error occurred. Please try again.";
      clientLogger.error("resume-check submission error", { message });
      setFatalError(message);
      setPhase("input");
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      <Header activePage="home" />

      <div className="flex-1 overflow-y-auto">
        <main className="flex-1">
          <AnimatePresence mode="wait">
            {phase === "input" && (
              <motion.div
                key="input"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {fatalError && (
                  <div className="max-w-2xl mx-auto px-6 pt-6">
                    <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                      {fatalError}
                    </div>
                  </div>
                )}
                <InputPhase
                  initialResumeText={resumeText}
                  initialJobDescription={jobDescription}
                  onSubmit={handleSubmit}
                />
              </motion.div>
            )}

            {phase === "scoring" && (
              <motion.div
                key="scoring"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-[60vh] flex items-center justify-center"
              >
                <div className="text-muted-foreground min-h-8 flex justify-center">
                  <RotatingMessages messages={SCORING_MESSAGES} animationStyle="typewriter" displayTimeMs={3000} />
                </div>
              </motion.div>
            )}

            {phase === "complete" && report && (
              <motion.div
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <ResultsPhase report={report} jobDescription={jobDescription} onBack={() => setPhase("input")} />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
        <Footer />
      </div>
    </div>
  );
}
